import { ChartBarIcon, ViewListIcon } from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarSolidIcon,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";

import { PromptMode } from "../../../InlineTemplateEditor";

import ConfigurationView from "./ConfigurationView";
import { usePromptTemplate } from "./hooks";

import { BlockProps, isEvalLikeEnv, useBlockEnv } from "@/components/PowerBlock/Block";
import { withBlockStore } from "@/components/PowerBlock/Block/block-store";
import BlockStatistics from "@/components/PowerBlock/Block/BlockStatistics";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import TabNavigation from "@/components/ui/TabNavigation";
import { InlineTemplate, PromptTemplateConfiguration } from "@/types/evaluate";

const validationSchema = createBlockSchema<PromptTemplateConfiguration>().requireName().build();

export type { PromptMode };

export const PromptTemplateBlockCore = observer(
  ({
    showVerboseMode = true,
    sources = createEmptySources(),
    id,
    readonly,
    forceOpenVariableSelector,
  }: Omit<BlockProps<PromptTemplateConfiguration>, "DefaultFields"> & {
    showVerboseMode?: boolean;
  }) => {
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const env = useBlockEnv();
    const isEval = isEvalLikeEnv(env);
    const {
      blockStore,
      promptTemplateVersionQuery,
      promptId,
      parseInputVariables,
      inputVariables,
      selectedPromptTemplateVersion,
      templateFormat,
      handleTemplateSelection,
      handleVerboseChange,
      handleReturnTemplateOnlyChange,
      handleHydrateInputVariablesChange,
      handleChatHistoryToggle,
      handleChatHistorySourceChange,
      handleMockToolResponsesToggle,
      handleMockScenarioChange,
      handleMockStopToolsChange,
      handleEngineUpdate,
      promptData,
      messages,
      defaultPromptData,
      releaseLabelExists,
    } = usePromptTemplate();

    // Determine mode based on configuration
    const mode: PromptMode = useMemo(() => {
      if (blockStore.configuration?.inline_template?.prompt_template) {
        return "inline";
      }
      return "registry";
    }, [blockStore.configuration?.inline_template?.prompt_template]);

    const handleModeChange = useCallback(
      (newMode: PromptMode) => {
        if (newMode === mode) {
          return;
        }

        if (newMode === "inline") {
          // Switch to inline mode - clear registry template, add default inline template
          blockStore.setConfiguration({
            ...blockStore.configuration,
            template: undefined,
            inline_template: {
              prompt_template: {
                type: "chat",
                input_variables: [],
                messages: [
                  {
                    role: "user",
                    content: [{ type: "text", text: "" }],
                    template_format: "jinja2",
                  },
                ],
              },
            },
            prompt_template_variable_mappings:
              blockStore.configuration?.prompt_template_variable_mappings || {},
          });
        } else {
          // Switch to registry mode - clear inline template, keep registry template
          blockStore.setConfiguration({
            ...blockStore.configuration,
            inline_template: undefined,
            prompt_template_variable_mappings:
              blockStore.configuration?.prompt_template_variable_mappings || {},
          });
        }
      },
      [blockStore, mode],
    );

    const handleInlineTemplateChange = useCallback(
      (inlineTemplate: InlineTemplate) => {
        blockStore.setConfiguration({
          ...blockStore.configuration,
          inline_template: inlineTemplate,
          prompt_template_variable_mappings:
            blockStore.configuration?.prompt_template_variable_mappings || {},
        });
      },
      [blockStore],
    );

    const handleTabChange = (tabId: number) => {
      setCurrentTabIndex(tabId);
    };

    const isChat =
      mode === "inline"
        ? blockStore.configuration?.inline_template?.prompt_template?.type === "chat"
        : promptTemplateVersionQuery.data?.prompt_template?.type === "chat";

    const handleAutofill = useCallback(
      (mappings: Record<string, string>) => {
        if (blockStore.configuration) {
          blockStore.setConfiguration({
            ...blockStore.configuration,
            prompt_template_variable_mappings: {
              ...blockStore.configuration.prompt_template_variable_mappings,
              ...mappings,
            },
          });
        }
      },
      [blockStore],
    );

    const configurationContent = (
      <ConfigurationView
        mode={mode}
        onModeChange={handleModeChange}
        templateName={blockStore.configuration?.template?.name}
        templateId={blockStore.configuration?.template?.id}
        promptId={promptId}
        isLoading={Boolean(
          promptId && promptTemplateVersionQuery.isLoading && !promptTemplateVersionQuery.data,
        )}
        isRefetching={promptTemplateVersionQuery.isRefetching}
        promptData={promptData}
        messages={messages}
        folderId={selectedPromptTemplateVersion?.folder_id ?? null}
        defaultPromptData={defaultPromptData}
        isChat={isChat}
        inputVariables={inputVariables}
        sources={sources}
        parseInputVariablesIsLoading={parseInputVariables.isLoading}
        showVerboseMode={showVerboseMode}
        verboseEnabled={blockStore.configuration?.verbose || false}
        returnTemplateOnlyEnabled={blockStore.configuration?.return_template_only || false}
        hydrateInputVariablesEnabled={blockStore.configuration?.hydrate_input_variables !== false}
        templateFormat={templateFormat}
        onTemplateSelection={handleTemplateSelection}
        onEngineUpdate={handleEngineUpdate}
        onVerboseChange={handleVerboseChange}
        onReturnTemplateOnlyChange={handleReturnTemplateOnlyChange}
        onHydrateInputVariablesChange={handleHydrateInputVariablesChange}
        chatHistorySource={blockStore.configuration?.chat_history_source}
        onChatHistoryToggle={handleChatHistoryToggle}
        onChatHistorySourceChange={handleChatHistorySourceChange}
        mockToolResponsesEnabled={blockStore.configuration?.mock_tool_responses || false}
        mockScenario={blockStore.configuration?.mock_scenario}
        mockStopTools={blockStore.configuration?.mock_stop_tools}
        onMockToolResponsesToggle={handleMockToolResponsesToggle}
        onMockScenarioChange={handleMockScenarioChange}
        onMockStopToolsChange={handleMockStopToolsChange}
        onAutofill={handleAutofill}
        currentVersionNumber={blockStore.configuration?.template?.version_number ?? undefined}
        currentLabel={blockStore.configuration?.template?.label}
        releaseLabelExists={releaseLabelExists.data?.exists}
        isCheckingLabel={releaseLabelExists.isLoading}
        promptError={promptTemplateVersionQuery.error}
        inlineTemplate={blockStore.configuration?.inline_template}
        onInlineTemplateChange={handleInlineTemplateChange}
        forceOpenVariableSelector={forceOpenVariableSelector}
      />
    );

    return (
      <>
        {readonly && isEval ? (
          <TabNavigation
            selectedIndex={currentTabIndex}
            handleTabChange={handleTabChange}
            tabs={[
              {
                id: 0,
                name: "Overview",
                outlineIcon: <ViewListIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                solidIcon: <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                content: <div className="pt-2">{configurationContent}</div>,
              },
              {
                id: 1,
                name: "Statistics",
                outlineIcon: <ChartBarIcon className="h-4 w-4" />,
                solidIcon: <ChartBarSolidIcon className="h-4 w-4" />,
                content: <BlockStatistics id={id as number} />,
              },
            ]}
          />
        ) : (
          configurationContent
        )}
      </>
    );
  },
);

export default withBlockStore<PromptTemplateConfiguration>(PromptTemplateBlockCore, {
  validationSchema,
});
